import {
  Box,
  Grid,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Radio,
  FormLabel,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useState } from "react";
import moment from "moment";
import GCAutocompleteCheckboxFixSizeUid from "../VendorDiscontinueChangeRequest/GCAutocompleteCheckboxFixSizeUid";
import { useSelector } from "react-redux";
import { CmpDatePickerField001V1 } from "../../Component/Common/ToolBox/ToolBoxV1";
import { fAlertToast } from "../../Utility/Utilitys";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";

// Generate URL query parameters
const generateQueryParams = (filterObj) => {
  const {
    fromDate,
    toDate,
    branch,
    excludeYearEndJV,
    consolidated,
    reportType,
  } = filterObj;
  const formattedFromDate = fromDate.format("MM/DD/YYYY");
  const formattedToDate = toDate.format("MM/DD/YYYY");

  return {
    v_Br: branch,
    v_Date1: formattedFromDate,
    v_Date2: formattedToDate,
    chkExcYrEdJV: excludeYearEndJV,
    chkConsolidated: consolidated,
    prntyp: reportType,
  };
};

const FinanceTrialBalanceReport = () => {
  const branchData = useSelector((state) => state.SliceDB.value.DbData.Branch);
  const kernOBj = useSelector((state) => state.SliceDB.value.DBKernObj);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isMadinaGroup =
    (kernOBj?.MADINA_GROUP?.Val || "").toUpperCase() === "Y";

  const [filterObj, setFilterObj] = useState({
    branch: "",
    excludeYearEndJV: false,
    consolidated: false,
    reportType: "trialBalance",
    fromDate: dayjs().startOf("year"),
    toDate: dayjs(),
    reportTypeOption: "closing",
  });

  const [reportUrl, setReportUrl] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const handleFilterChange = (key, value) => {
    setFilterObj((prev) => ({ ...prev, [key]: value }));
  };

  const handleViewReport = () => {
    if (!filterObj.fromDate || !filterObj.toDate)
      return fAlertToast("FAILED", "Date should not be empty");
    if (!filterObj.branch)
      return fAlertToast("FAILED", "Branch should not be empty");

    const q = generateQueryParams(filterObj);
    const base = kernOBj?.WEB_REPORT_BASE_URL?.Val;
    setReportUrl(
      `${base}WMS/TBReport?Br=${q.v_Br}&Date1=${q.v_Date1}&Date2=${q.v_Date2}&chkExcYrEdJV=${q.chkExcYrEdJV}&chkConsolidated=${q.chkConsolidated}&prntyp=${q.prntyp}`
    );
    setShowFilters(false);
  };

  const handleReset = () => {
    setFilterObj({
      branch: "",
      excludeYearEndJV: false,
      consolidated: false,
      reportType: "trialBalance",
      fromDate: dayjs().startOf("year"),
      toDate: dayjs(),
      reportTypeOption: "closing",
    });
    setReportUrl("");
  };

  const handleCheckboxChange = (event) => {
    handleFilterChange("reportType", event.target.value);
  };

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
        <IconButton
          onClick={() => setShowFilters((prev) => !prev)}
          sx={{ mb: 1 }}
          color="primary"
        >
          {showFilters ? <CloseIcon /> : <FilterAltIcon />}
        </IconButton>
      </Tooltip>

      <Grid container spacing={1} alignItems="flex-start">
        {showFilters && (
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography>From</Typography>
                  <CmpDatePickerField001V1
                    xLabelText=""
                    xName="fromDate"
                    xValue={filterObj.fromDate}
                    xOnChange={(val) => handleFilterChange("fromDate", val)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography>To</Typography>
                  <CmpDatePickerField001V1
                    xLabelText=""
                    xName="toDate"
                    xValue={filterObj.toDate}
                    xOnChange={(val) => handleFilterChange("toDate", val)}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography>Branch</Typography>
              <GCAutocompleteCheckboxFixSizeUid
                xLabelText="Branch"
                xValue={filterObj.branch}
                setValue={(val) => handleFilterChange("branch", val)}
                options={branchData}
                objName="branch"
                placeholder="Search Branch"
              />

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterObj.excludeYearEndJV}
                    onChange={(e) =>
                      handleFilterChange("excludeYearEndJV", e.target.checked)
                    }
                  />
                }
                label="Exclude YearEnd-JV"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterObj.consolidated}
                    onChange={(e) =>
                      handleFilterChange("consolidated", e.target.checked)
                    }
                  />
                }
                label="Consolidated"
              />

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={1}>
                <Grid container alignItems="center">
                  <Grid item xs={3}></Grid>
                  {[
                    "Closing",
                    "Standard",
                    "With Budget",
                    isMadinaGroup && "Monthly Progress",
                    isMadinaGroup && "Monthly Diff",
                  ]
                    .filter(Boolean)
                    .map((label, i) => (
                      <Grid item xs={2} key={i} sx={{ textAlign: "center" }}>
                        <FormLabel
                          sx={{ fontSize: "10px", fontWeight: "bold" }}
                        >
                          {label.split(" ").map((word, idx) => (
                            <div key={idx}>{word}</div>
                          ))}
                        </FormLabel>
                      </Grid>
                    ))}
                </Grid>

                {[
                  { label: "Trial Balance", prefix: "TB" },
                  { label: "Balance Sheet", prefix: "BS" },
                  { label: "Profit and Loss", prefix: "PL" },
                ].map((report, idx) => (
                  <Grid container key={idx} alignItems="center">
                    <Grid item xs={3}>
                      <FormLabel sx={{ fontSize: "10px" }}>
                        {report.label}
                      </FormLabel>
                    </Grid>
                    {[
                      `${report.prefix}Closing`,
                      report.prefix === "BS"
                        ? `${report.prefix}CrDr`
                        : `${report.prefix}DrCr`,
                      `${report.prefix}Budget`,
                      isMadinaGroup && `${report.prefix}Month`,
                      isMadinaGroup && `${report.prefix}MonthlyDiff`,
                    ]
                      .filter(Boolean)
                      .map((val, j) => (
                        <Grid item xs={2} key={j} sx={{ textAlign: "center" }}>
                          <Radio
                            value={val}
                            checked={filterObj.reportType === val}
                            onChange={handleCheckboxChange}
                            size="small"
                          />
                        </Grid>
                      ))}
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleViewReport}
              >
                View Report
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={handleReset}
                sx={{ mt: 2 }}
              >
                Reset
              </Button>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={showFilters ? 9 : 12}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: isMdUp ? "calc(100vh - 122px)" : "auto",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Trial Balance Report
            </Typography>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              {reportUrl ? (
                <iframe
                  src={reportUrl}
                  title="Trial Balance Report"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    display: "block",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    border: "1px dashed grey",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                  }}
                >
                  Report preview area
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinanceTrialBalanceReport;
